/**
 * Confidence level utilities for parts similarity matching.
 *
 * AIDEV-NOTE: This module handles backwards compatibility between
 * legacy numeric confidence (0-100) and new categorical levels.
 * Existing sessions store numeric values in rawReport.partSimilarities.
 */

export type ConfidenceLevel = "high" | "medium" | "low";

/**
 * Normalizes confidence from numeric (legacy) or string to categorical level.
 * Handles backwards compatibility with existing sessions that have numeric data.
 *
 * @param confidence - Either a categorical level string or legacy numeric value
 * @returns Normalized categorical confidence level
 */
export function normalizeConfidence(
    confidence: ConfidenceLevel | number,
): ConfidenceLevel {
    if (typeof confidence === "string") {
        if (["high", "medium", "low"].includes(confidence)) {
            return confidence as ConfidenceLevel;
        }
        // Invalid string defaults to low
        return "low";
    }

    // Legacy numeric confidence - handle both 0-1 and 0-100 scales
    const normalizedValue = confidence > 1 ? confidence / 100 : confidence;
    if (normalizedValue >= 0.8) return "high";
    if (normalizedValue >= 0.6) return "medium";
    return "low";
}

/**
 * Maps confidence level to Badge component variant.
 */
export function getConfidenceBadgeVariant(
    confidence: ConfidenceLevel | number,
): "default" | "secondary" | "outline" {
    const level = normalizeConfidence(confidence);
    switch (level) {
        case "high":
            return "default";
        case "medium":
            return "secondary";
        case "low":
            return "outline";
    }
}

/**
 * Maps confidence level to human-readable display label.
 */
export function getConfidenceLabel(
    confidence: ConfidenceLevel | number,
): string {
    const level = normalizeConfidence(confidence);
    switch (level) {
        case "high":
            return "High";
        case "medium":
            return "Medium";
        case "low":
            return "Low";
    }
}

/**
 * Whether this confidence level should be visually de-emphasized in the UI.
 */
export function shouldDeemphasize(
    confidence: ConfidenceLevel | number,
): boolean {
    return normalizeConfidence(confidence) === "low";
}

/**
 * Convert confidence level to a numeric value for comparison purposes.
 * Used in tests and analytics - high=3, medium=2, low=1
 */
export function confidenceToNumeric(
    confidence: ConfidenceLevel | number,
): number {
    const level = normalizeConfidence(confidence);
    switch (level) {
        case "high":
            return 3;
        case "medium":
            return 2;
        case "low":
            return 1;
    }
}

/**
 * Check if confidence meets or exceeds a minimum level.
 */
export function confidenceMeetsThreshold(
    confidence: ConfidenceLevel | number,
    minLevel: ConfidenceLevel,
): boolean {
    return confidenceToNumeric(confidence) >= confidenceToNumeric(minLevel);
}
